/**
 * ENG-8573 (slice 1 of ENG-8427): extract tool-call audit records from a Claude
 * Code session transcript.
 *
 * PURE BY CONSTRUCTION. This module reads transcript files and returns records.
 * It does not resolve a cursor, does not consult a feature flag, does not check
 * entitlement and — above all — does not POST anything. Slice 2 (ENG-8574) owns
 * the cursor contract and slice 3 (ENG-8575) arms it. Until then nothing here
 * can run in production, which is deliberate: the redaction rules get merged and
 * reviewed before anything is capable of emitting a record anywhere.
 *
 * WHY A TRANSCRIPT SCAN AND NOT THE PostToolUse HOOK. Settled on ENG-8426/8427
 * and repeated here because it is the load-bearing decision: the plugin's hook
 * matcher is an ENUMERATED tool-name allowlist and every hook command ends
 * `2>/dev/null || true`. An audit log whose gaps are invisible is not an audit
 * log. Deriving the index from the same artefact that ENG-8428 serves as
 * evidence also means a row can never point at something that does not exist.
 *
 * WHAT THE IDENTITY IS. `tool_use.id` (`toolu_…`), taken from the transcript
 * rather than synthesised. Measured across 12 real transcripts (152,359 lines /
 * 23,133 calls): present on 100% of calls with zero duplicates. Re-measured for
 * this slice across one session's main transcript plus its 13 sub-agent
 * sidecars (780 calls): still zero duplicates ACROSS files, which is what makes
 * the (agent_id, session_id, tool_use_id) uniqueness key safe even though a
 * sidecar shares its parent's session_id.
 */

import { closeSync, fstatSync, openSync, readFileSync, readSync, readdirSync } from 'node:fs';
import { basename, join, relative } from 'node:path';
import { StringDecoder } from 'node:string_decoder';

import { findRunMarker } from '@augmented/core';

import {
  REDACTION_RULE_VERSION,
  hashToolInput,
  redactToolTarget,
  type RedactedTargetKind,
  type RedactionContext,
} from './tool-call-redaction.js';

/** Bump when the SHAPE of what is extracted changes (fields, pairing, refs). */
export const EXTRACTOR_VERSION = 'e1';

/**
 * Where this host archives transcripts, captured from the host and passed in
 * VERBATIM.
 *
 * This is a value object on purpose: the extractor is given a key prefix and
 * can only copy it. It has no ability to compose one, which is the structural
 * form of "captured, never reconstructed" (ENG-8573 AC3).
 *
 * Reconstructing would be wrong in two independent ways. First, the archiver
 * builds `key_prefix="${ORG_ID:+${ORG_ID}/}${instance_arn}"` — the org segment
 * is CONDITIONAL, so an org-less host archives to `<arn>/…` with no org segment
 * and a reconstructed `<org>/<arn>/<rel>` would 404 for exactly those hosts.
 * Second, ENG-8438 changed the instance segment from a bare instance id to the
 * full instance ARN, and legacy objects still carry the old shape. Treating the
 * prefix as an opaque string is immune to both: this module never parses it, so
 * there is no shape for it to be wrong about.
 *
 * A missing evidence object reads as "the agent did nothing", which is the
 * exact ambiguity this feature exists to remove — hence null rather than a
 * guess when the address is unknown.
 */
export interface HostArchiveAddress {
  bucket: string | null;
  region: string | null;
  instanceId: string | null;
  /** Opaque. Object key = `${keyPrefix}/${transcript_rel_path}`. */
  keyPrefix: string | null;
}

/** One extracted tool call, shaped for POST /host/tool-calls (ENG-8426). */
export interface ToolCallRecord {
  tool_use_id: string;
  tool_name: string;
  /**
   * NULL means the transcript record carried no root `timestamp`.
   *
   * Deliberately NOT defaulted to the epoch. A fabricated 1970 timestamp is
   * indistinguishable from a real one once it is in the table and sorts to the
   * front of every timeline — the same "unknown rendered as determined" failure
   * that `is_error` is so careful to avoid. Null keeps the gap visible, and it
   * surfaces through machinery that already exists: the ingest route rejects an
   * unparseable `occurred_at` and counts it with a reason, so a producer that
   * starts emitting these is loud rather than silently wrong.
   *
   * Never observed — 25,333 tool_use records across 61 transcripts all carried a
   * root timestamp — so this is a defensive branch, not an expected case.
   */
  occurred_at: string | null;
  session_id: string | null;
  transcript_rel_path: string;
  transcript_ref: string;
  line_offset: number;
  is_subagent: boolean;
  subagent_id: string | null;
  /** NULL means NOT DETERMINED. Never render it as success. */
  is_error: boolean | null;
  /**
   * The run this call was made inside, or NULL for NOT DETERMINED (ENG-8832).
   *
   * DERIVED FROM THE TRANSCRIPT'S OWN ORDER, NEVER FROM A CLOCK. The manager
   * writes an inert `<!-- agt-run:UUID -->` marker (ENG-5565) into the turn that
   * opens each injected run. This walk carries the marker in scope forward until
   * the next marker, exactly as ENG-5566's token-attribution parser does. No
   * comparison is ever made between a run's `started_at`/`ended_at` and a call's
   * `occurred_at`: runs overlap, sessions resume across runs, and host/control-
   * plane clock skew is real, so a time-window match would place actions in runs
   * that did not produce them — and on an audit surface a wrong attribution is
   * worse than none.
   *
   * NULL IS NOT "INTERACTIVE" AND NOT "NO RUN". It is the honest answer for
   * every case the transcript cannot settle, and there are four routine ones:
   *   * ambient / human-directed work before any marker has been seen;
   *   * sub-agent sidecars, which carry no markers of their own — a delegated
   *     worker's calls are deliberately NOT given the parent's scope, because
   *     inheriting it would be an inference, not an observation;
   *   * a scan that resumed mid-file before any marker in its window (the cursor
   *     carries the scope across ticks, but a first-run seek starts with none);
   *   * every row ingested before this field existed.
   *
   * KNOWN BOUND, so nobody adopts this thinking it is tighter than it is: the
   * scope closes at the NEXT marker, not when the injected work finished. Work
   * an operator directs in the quiet after a scheduled task, and before the next
   * injection, is attributed to that task's run. Consumers that must not
   * over-claim autonomy should treat this as "the run in scope", not as proof
   * nobody was watching.
   */
  run_id: string | null;
  input_sha256: string | null;
  redacted_target: string | null;
  redacted_target_kind: RedactedTargetKind;
  extractor_version: string;
  redaction_rule_version: string;
  archive_bucket: string | null;
  archive_region: string | null;
  host_instance_id: string | null;
  archive_key_prefix: string | null;
}

export interface ExtractOptions {
  /** Root of the agent's transcripts, i.e. `sessionTranscriptDir(projectDir)`. */
  transcriptDir: string;
  archive: HostArchiveAddress | null;
  redaction: RedactionContext;
}

/** A transcript file to scan, with the discriminator its records inherit. */
export interface TranscriptFile {
  absPath: string;
  /** Path relative to `~/.claude/projects` — the archiver's `rel`. */
  relPath: string;
  /** `'main'` or `subagent:agent-<id>`. */
  ref: string;
  isSubagent: boolean;
  subagentId: string | null;
}

/**
 * Enumerate a session's transcript files: the main JSONL plus every sub-agent
 * sidecar under `<sessionId>/subagents/`.
 *
 * The sidecars matter more than their obscurity suggests. Claude Code writes a
 * delegated worker's turns to a DIFFERENT FILE under the SAME `sessionId`
 * (daily-session.ts:340), and in the session measured for this slice they held
 * 192 of 780 tool calls — a quarter of the audit surface, weighted towards the
 * Bash and Edit calls most worth auditing. Because they share the parent's
 * session_id, `line_offset` alone collides head-on with the parent's, which is
 * why every record carries `transcript_ref` as well.
 */
export function enumerateSessionTranscripts(
  transcriptDir: string,
  sessionId: string,
  projectsRoot: string,
): TranscriptFile[] {
  const files: TranscriptFile[] = [];

  const mainAbs = join(transcriptDir, `${sessionId}.jsonl`);
  files.push({
    absPath: mainAbs,
    relPath: relative(projectsRoot, mainAbs),
    ref: 'main',
    isSubagent: false,
    subagentId: null,
  });

  const subDir = join(transcriptDir, sessionId, 'subagents');
  let entries: string[];
  try {
    entries = readdirSync(subDir);
  } catch {
    // No subagents dir is the normal case, not an error.
    return files;
  }
  for (const name of entries) {
    if (!name.endsWith('.jsonl')) continue; // skips the `.meta.json` siblings
    const abs = join(subDir, name);
    const stem = basename(name, '.jsonl'); // `agent-<id>`
    files.push({
      absPath: abs,
      relPath: relative(projectsRoot, abs),
      ref: `subagent:${stem}`,
      isSubagent: true,
      subagentId: stem.startsWith('agent-') ? stem.slice('agent-'.length) : stem,
    });
  }
  return files;
}

/** A `tool_use` seen but not yet paired with its result. */
interface PendingCall {
  record: ToolCallRecord;
  /**
   * Byte offset of the line this `tool_use` was read from.
   *
   * Only meaningful on the windowed path (ENG-8574), which needs to rewind the
   * cursor to the EARLIEST still-unpaired call so a later scan can pair it. The
   * whole-file path has no cursor to rewind and leaves this 0.
   */
  byteStart: number;
}

/**
 * Extract every tool call from one transcript file.
 *
 * PAIRING. A `tool_use` block in an assistant record is matched to the
 * `tool_result` block carrying the same `tool_use_id`, which arrives in a later
 * user record. Both may appear in the same file; a result may also be absent
 * entirely when the scan reaches the tail mid-turn.
 *
 * `is_error` SEMANTICS — the part that is easy to get backwards:
 *   * result present with `is_error: true`  → true
 *   * result present with `is_error: false` → false
 *   * result present, `is_error` ABSENT     → false (the API's default)
 *   * result present with `is_error: null`  → null (the producer said unknown)
 *   * NO result                             → null (NOT DETERMINED)
 *
 * The third case looks like it should be null and is not, and the difference
 * matters because absence is the COMMON case: measured over three real
 * transcripts, 313 of 767 results (41%) omitted the key entirely. Claude Code
 * writes `is_error` explicitly on every Bash result and omits it on success for
 * most other tools. Reading absence as "not determined" would therefore leave
 * is_error NULL for nearly every non-Bash call and quietly hollow out the
 * column. The evidence that absence really is success: across 61 transcripts
 * `is_error: true` appears for 15 distinct tools including Edit (120), Read,
 * Write and several MCP tools — so failures DO carry the flag whatever the tool,
 * and its absence is the API's documented default, not a shape gap.
 *
 * The two null cases are the only genuinely unknown ones, and the distinction
 * between "absent" and "explicitly null" is load-bearing: absence is the API's
 * default and means success, while an explicit null is the producer declining to
 * say. Collapsing them would turn "I don't know" into "it worked".
 */
export function extractFromTranscript(
  file: TranscriptFile,
  opts: ExtractOptions,
): ToolCallRecord[] {
  let content: string;
  try {
    content = readFileSync(file.absPath, 'utf-8');
  } catch {
    // A transcript that vanished (archived, rotated, or never existed) yields
    // no records. The caller distinguishes "no calls" from "could not read" by
    // the file list, not by an exception here.
    return [];
  }

  const lines = content.split('\n');
  return scanLines(
    (function* () {
      for (let i = 0; i < lines.length; i++) yield [i, lines[i]!, 0] as const;
    })(),
    file,
    opts,
  ).records;
}

/**
 * The shared line walk, over any source of `[lineOffset, text, byteStart]`.
 *
 * Extracted from `extractFromTranscript` (ENG-8574) so the whole-file reader and
 * the resumable windowed reader below cannot diverge in how they pair blocks or
 * build records. Two walks over the same JSONL would be two chances to disagree
 * about what a tool call is, and the disagreement would show up as a coverage
 * gap that nothing reports.
 *
 * Returns the still-unpaired calls alongside the records, because pairing cannot
 * be recovered from a record afterwards: `is_error` is null both for a call
 * whose result has not arrived AND for one whose result explicitly said null.
 * The caller's cursor decision depends on telling those apart.
 */
function scanLines(
  lines: Iterable<readonly [number, string, number]>,
  file: TranscriptFile,
  opts: ExtractOptions,
  initialRunId: string | null = null,
): { records: ToolCallRecord[]; pending: Map<string, PendingCall>; endRunId: string | null } {
  const pending = new Map<string, PendingCall>();
  const ordered: ToolCallRecord[] = [];
  // ENG-8832. The run scope in effect, carried in from the previous window so a
  // resumed scan does not restart unattributed in the middle of a run.
  let currentRunId: string | null = initialRunId;

  for (const [lineOffset, rawLine, byteStart] of lines) {
    const trimmed = rawLine.trim();
    if (trimmed.length === 0) continue;

    let record: Record<string, unknown>;
    try {
      record = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      // A partial trailing line (the file is appended to while we read) or a
      // corrupt record. Skip it — one unparseable line must not abort the scan
      // and lose every call after it.
      continue;
    }
    if (typeof record !== 'object' || record === null) continue;

    const message = record.message;

    // ENG-8832: a run marker in a user record opens (or switches) the scope that
    // every following call inherits. Checked BEFORE the block walk and BEFORE
    // the array guard below, because the marker can arrive as a plain string
    // `message.content` (the tmux send-keys lane) as well as inside blocks (the
    // durable direct-chat notice rail) — and the array guard would drop the
    // string case on the floor.
    //
    // Sub-agent sidecars are excluded outright rather than incidentally: they
    // carry no injected turns, so any marker appearing in one arrived as tool
    // OUTPUT. Honouring it would let a delegated worker's file reading rewrite
    // the parent session's attribution.
    if (record.type === 'user' && !file.isSubagent && message && typeof message === 'object') {
      const marked = findRunMarker((message as { content?: unknown }).content);
      if (marked) currentRunId = marked;
    }

    const blocks =
      message && typeof message === 'object' && Array.isArray((message as { content?: unknown }).content)
        ? ((message as { content: unknown[] }).content)
        : null;
    if (!blocks) continue;

    for (const raw of blocks) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
      const block = raw as Record<string, unknown>;

      if (block.type === 'tool_use') {
        const id = typeof block.id === 'string' ? block.id : null;
        const name = typeof block.name === 'string' ? block.name : null;
        if (!id || !name) continue;

        const { target, kind } = redactToolTarget(name, block.input, opts.redaction);
        const occurredAt = typeof record.timestamp === 'string' ? record.timestamp : null;

        const built: ToolCallRecord = {
          tool_use_id: id,
          tool_name: name,
          occurred_at: occurredAt,
          session_id: typeof record.sessionId === 'string' ? record.sessionId : null,
          transcript_rel_path: file.relPath,
          transcript_ref: file.ref,
          line_offset: lineOffset,
          // The FILE is the sole authority, deliberately — not `record.isSidechain`.
          //
          // These three fields are one fact stated three ways and must not be
          // able to contradict each other: `is_subagent` true iff
          // `transcript_ref` starts with `subagent:` iff `subagent_id` is set.
          // Letting `record.isSidechain` raise `is_subagent` on its own could
          // produce is_subagent=true with transcript_ref='main' and a null
          // subagent_id — a row claiming a delegated worker made the call while
          // addressing the parent's transcript. `transcript_ref` is an EVIDENCE
          // ADDRESS: it must name the file the line actually lives in, so the
          // file has to win and the other two follow it. (CodeRabbit.)
          //
          // Nothing is lost today: no main-transcript record in the measured
          // corpus carried isSidechain on a tool_use, and every sidecar record
          // did. If Claude Code ever inlines sidechain turns into the main file,
          // record-level sidechain-ness needs its own field rather than
          // overloading this one.
          is_subagent: file.isSubagent,
          subagent_id: file.isSubagent
            ? typeof record.agentId === 'string' && record.agentId
              ? record.agentId
              : file.subagentId
            : null,
          is_error: null, // resolved on pairing below
          run_id: currentRunId,
          input_sha256: hashToolInput(block.input),
          redacted_target: target,
          redacted_target_kind: kind,
          extractor_version: EXTRACTOR_VERSION,
          redaction_rule_version: REDACTION_RULE_VERSION,
          archive_bucket: opts.archive?.bucket ?? null,
          archive_region: opts.archive?.region ?? null,
          host_instance_id: opts.archive?.instanceId ?? null,
          archive_key_prefix: opts.archive?.keyPrefix ?? null,
        };
        ordered.push(built);
        pending.set(id, { record: built, byteStart });
        continue;
      }

      if (block.type === 'tool_result') {
        const id = typeof block.tool_use_id === 'string' ? block.tool_use_id : null;
        if (!id) continue;
        const call = pending.get(id);
        if (!call) continue; // a result whose tool_use is before our window
        // ABSENT means the API's default, i.e. success. An EXPLICIT null is the
        // producer saying "not determined" and must stay null — `is_error ===
        // true` alone would collapse it to success, the one reading the column's
        // contract forbids. Never observed (0 of 25,335 results across 61
        // transcripts carry an explicit null), but the cost of being wrong here
        // is an audit row asserting a success nobody claimed. (CodeRabbit.)
        call.record.is_error =
          'is_error' in block && block.is_error === null ? null : block.is_error === true;
        pending.delete(id);
      }
    }
  }

  return { records: ordered, pending, endRunId: currentRunId };
}

/** Where a windowed scan stopped, and what it could not finish. */
export interface TranscriptWindow {
  records: ToolCallRecord[];
  /**
   * Byte offset just past the last COMPLETE line consumed — the resume point.
   *
   * A transcript is appended to while we read it, so the final bytes are
   * routinely half a line. Consuming them would parse-fail (harmless) but
   * advancing past them would skip the rest of that line forever once the writer
   * finishes it (not harmless). The window therefore ends at the last newline.
   */
  endByte: number;
  /** Line ordinal just past the last complete line, for `line_offset` continuity. */
  endLine: number;
  /**
   * Calls seen in this window whose `tool_result` had not arrived yet, earliest
   * first. Their `is_error` is null for "not yet known", NOT for "no verdict".
   */
  unpaired: {
    toolUseId: string;
    byteStart: number;
    lineOffset: number;
    /**
     * The run scope in effect AT THIS CALL (ENG-8832) — not at the end of the
     * window. When the cursor rewinds to re-pair this call, this is the scope
     * the re-read must resume with, and it is the only value that is exactly
     * right: an unpaired call is the one row in the window that was NOT sent, so
     * `ignoreDuplicates` will not protect it, and a marker landing between it
     * and the window's end would otherwise file it under the following run.
     */
    runScopeId: string | null;
  }[];
  /** The file could not be opened. Distinct from "opened and had no calls". */
  missing: boolean;
  /** The file is SHORTER than the resume point — rotated, replaced or truncated. */
  rewound: boolean;
  /**
   * The run scope in effect at `endByte`, to be carried into the next window
   * (ENG-8832). Null when no marker has been seen.
   *
   * WHY END-OF-WINDOW IS THE RIGHT SCOPE TO PERSIST, even though the resume
   * point can be rewound behind it. A rewind exists to re-pair an unpaired call,
   * so it re-reads lines whose rows were ALREADY ingested — and ingest is
   * `ignoreDuplicates`, so a re-emitted row's `run_id` is discarded and the
   * originally-stored value stands. The only rows whose `run_id` this value
   * decides are those beyond the previous window's end, and there the
   * end-of-window scope is exactly the scope in force.
   */
  endRunScopeId: string | null;
  /**
   * The window stopped on its BYTE BUDGET with more file left, not at EOF
   * (ENG-8740).
   *
   * The caller needs this to tell two states apart that the offsets alone
   * cannot: "scanned this file to its end" and "scanned as far as one tick is
   * allowed to". Only the first justifies advancing a coverage watermark —
   * treating the second as complete is how a partially-scanned file comes to
   * claim it was covered.
   */
  truncated: boolean;
}

/** Read size. Large enough that a 8.7 MB transcript is ~140 reads, not 140k. */
const WINDOW_CHUNK_BYTES = 64 * 1024;

/**
 * How many bytes of COMPLETE LINES one call may consume (ENG-8740).
 *
 * This is the bound the function's contract always claimed and never had.
 * `WINDOW_CHUNK_BYTES` bounds a single `readSync`; before this constant existed
 * the loop kept going to EOF and held every line, so memory grew with the number
 * of UNSCANNED bytes and — because the read is synchronous — the manager tick
 * blocked for the whole of it. Two prod hosts hold ~560 MB of un-swept
 * transcripts, and while `backfill` is at least a deliberate choice, the rewind
 * below is NOT opt-in: a rotated or truncated transcript resets the start to 0
 * on its own.
 *
 * 4 MiB is chosen against the tick, not against memory: `scanLines` JSON-parses
 * every line it is handed, and a few hundred milliseconds of parse is the most a
 * ~10s poll cycle can absorb without being noticed. A backlog is therefore drained
 * over several ticks, which is fine — transcripts persist 7 days locally, so
 * lagging by minutes costs nothing, whereas blocking the tick costs everything
 * else the manager does.
 */
export const WINDOW_BUDGET_BYTES = 4 * 1024 * 1024;

/**
 * Extract tool calls from one transcript starting at a byte offset.
 *
 * WHY BYTE OFFSETS AND NOT LINE NUMBERS. Resuming by line number still has to
 * read and discard every earlier line, so a long-lived host re-parses the same
 * 152,359 lines on every scan. Seeking by byte makes a steady-state scan
 * proportional to what was appended since the last one. `line_offset` is still
 * emitted on every record — it is the evidence address within the file — which
 * is why the cursor carries both and they advance together.
 *
 * WHY COMPLETE LINES ONLY. See `endByte`. The whole-file reader can afford to
 * hand a torn final line to `JSON.parse` and let it fail, because it starts from
 * zero every time and will see the finished line on the next pass. A resuming
 * reader cannot: whatever it steps over, it never revisits.
 *
 * Reads synchronously in chunks rather than via `readline`/`createReadStream`,
 * so this stays a sync function like the rest of the module.
 *
 * MEMORY IS BOUNDED BY THE BUDGET, NOT BY THE FILE (ENG-8740). One call consumes
 * at most `budgetBytes` of complete lines and then stops, reporting `truncated`
 * so the caller resumes on the next tick. A line that would take the total past
 * the budget is left for the next tick rather than taken and noticed afterwards.
 *
 * The ONE case that may exceed the budget is a single line longer than the whole
 * budget, and only when it is the first line of the window: the loop reads until
 * its newline arrives, because deferring it would consume nothing, advance
 * nothing, and re-read the same bytes forever.
 *
 * Stated exactly, because the version of this comment that shipped with ENG-8574
 * was wrong and nobody caught it. Retained bytes are at most
 * `max(budget, first line)` for the consumed lines, plus `one chunk + one line`
 * for the undrained tail of the last read. Every term is a constant except the
 * budget — which is the point, and is what "bounded by the chunk plus the longest
 * single line, not by the file" claimed while the code read to EOF.
 */
export function extractTranscriptWindow(
  file: TranscriptFile,
  opts: ExtractOptions,
  from: {
    byteOffset: number;
    lineOffset: number;
    budgetBytes?: number;
    /** Run scope carried from the previous window (ENG-8832). */
    runScopeId?: string | null;
  },
): TranscriptWindow {
  let fd: number;
  try {
    fd = openSync(file.absPath, 'r');
  } catch {
    // Vanished (archived, rotated, never existed). Same contract as
    // extractFromTranscript: no records, no throw.
    return {
      records: [],
      endByte: from.byteOffset,
      endLine: from.lineOffset,
      unpaired: [],
      missing: true,
      rewound: false,
      // Unread, so nothing was learned and nothing may be revised: hand the
      // caller's own scope back rather than reporting null, which would read as
      // "the scope ended" and un-attribute the next window's calls.
      endRunScopeId: from.runScopeId ?? null,
      truncated: false,
    };
  }

  // A cursor past EOF means the file we were reading is not the file that is
  // there now — Claude Code replaced it, or it was truncated. Re-reading from 0
  // is safe (ingest is idempotent on tool_use_id) and is the only way to avoid
  // silently skipping the whole of the new file.
  let startByte = from.byteOffset;
  let startLine = from.lineOffset;
  let rewound = false;
  // null when fstat failed, which is a "do not know" and must not be read as 0.
  let fileSize: number | null = null;
  try {
    const { size } = fstatSync(fd);
    fileSize = size;
    if (startByte > size) {
      startByte = 0;
      startLine = 0;
      rewound = true;
    }
  } catch {
    // fstat failing is not a reason to abandon the scan; carry on from the
    // recorded offset and let the read itself decide.
  }

  // The rewind above sets startByte back to 0 WITHOUT being asked to, so the
  // budget has to apply to it as much as to a backfill — it is the path most
  // likely to meet a large file, and the only one no operator chose.
  //
  // `Number.isFinite` first, and not merely for tidiness: `Math.max(1, NaN)` is
  // NaN, every `>= NaN` comparison below is false, and the loop would run to EOF
  // again — the exact unbounded read this function was changed to stop,
  // reinstated silently by one bad argument. Fall back to the default rather
  // than to no bound at all.
  const requested = from.budgetBytes;
  const budget =
    typeof requested === 'number' && Number.isFinite(requested) && requested >= 1
      ? Math.floor(requested)
      : WINDOW_BUDGET_BYTES;

  const decoder = new StringDecoder('utf8');
  const buf = Buffer.allocUnsafe(WINDOW_CHUNK_BYTES);
  let position = startByte;
  let runningByte = startByte;
  let lineOffset = startLine;
  let carry = '';
  let hitBudget = false;
  const pairs: (readonly [number, string, number])[] = [];

  try {
    for (;;) {
      // Measured on runningByte (COMPLETE lines consumed), not on position
      // (bytes read): a chunk that lands mid-line has not covered those bytes
      // yet, and ending the window on them would report a watermark ahead of
      // what was actually scanned.
      //
      // Checked before the read rather than after, so a window that has already
      // met its budget issues no further syscall. The consequence is that a
      // single line longer than the budget still completes — the check cannot
      // trip until at least one line has been consumed, which is exactly the
      // forward-progress guarantee: stopping mid-line would consume nothing and
      // re-read the same bytes on every tick, forever.
      if (runningByte - startByte >= budget) {
        hitBudget = true;
        break;
      }
      const bytesRead = readSync(fd, buf, 0, WINDOW_CHUNK_BYTES, position);
      if (bytesRead <= 0) break;
      position += bytesRead;
      carry += decoder.write(buf.subarray(0, bytesRead));

      let nl: number;
      while ((nl = carry.indexOf('\n')) !== -1) {
        const line = carry.slice(0, nl);
        // +1 for the newline itself. byteLength, not length: a transcript is
        // full of non-ASCII and a UTF-16 length would desynchronise the cursor
        // from the file — permanently, and in a direction that skips content.
        const lineBytes = Buffer.byteLength(line, 'utf8') + 1;
        const consumed = runningByte - startByte;

        // DECIDED BEFORE THE LINE IS TAKEN, which is what makes the budget a
        // bound on what is RETAINED rather than a limit noticed after the fact.
        //
        // Checking afterwards — the first version of this — bounds the overshoot
        // at "one line", which sounds tight and is not: a line is one JSON
        // record, and a large Read result or tool input can be megabytes. With a
        // 4 MiB budget, 3 MiB already consumed and a 3 MiB line next, the window
        // would take 6 MiB while no single line exceeded the budget. Deferring
        // the line keeps the consumed total at or under the budget instead.
        // (CodeRabbit.)
        //
        // `consumed > 0` is the forward-progress guarantee: the FIRST line is
        // always taken however large it is, because deferring it would consume
        // nothing, advance nothing, and re-read the same bytes on every tick
        // forever. An oversized line is the one case that may exceed the budget,
        // and it can only ever be the first one.
        if (consumed > 0 && consumed + lineBytes > budget) {
          hitBudget = true;
          break;
        }

        pairs.push([lineOffset, line, runningByte] as const);
        runningByte += lineBytes;
        lineOffset += 1;
        carry = carry.slice(nl + 1);
      }
      if (hitBudget) break;
    }
  } catch {
    // A read error mid-file leaves everything consumed so far intact; the cursor
    // simply stops where it stopped and the next scan retries from there.
  } finally {
    try {
      closeSync(fd);
    } catch {
      /* already closed / never valid */
    }
  }
  // Anything still in `carry` is an unterminated final line. Deliberately not
  // consumed and deliberately not counted into runningByte.

  // A rewind means this is a DIFFERENT FILE under the same key — rotated,
  // replaced or truncated — and the read restarts at byte 0. The scope carried
  // from the old file describes bytes that no longer exist, so it is dropped and
  // re-derived from the new file's own markers. Keeping it would attribute the
  // head of a fresh transcript to a run that ended in a file that is gone.
  const carriedScope = rewound ? null : (from.runScopeId ?? null);
  const { records, pending, endRunId } = scanLines(pairs, file, opts, carriedScope);

  const unpaired = [...pending.values()]
    .map((p) => ({
      toolUseId: p.record.tool_use_id,
      byteStart: p.byteStart,
      lineOffset: p.record.line_offset,
      runScopeId: p.record.run_id,
    }))
    .sort((a, b) => a.byteStart - b.byteStart);

  // `truncated` means "stopped early with more to read", so a budget met exactly
  // at EOF is NOT truncated. Without the size comparison the last window of
  // every large file would report partial and hold its coverage watermark back
  // a tick for no reason. When fstat failed we do not know, and the honest
  // answer is the pessimistic one: claim less coverage, not more.
  const truncated = hitBudget && (fileSize === null || runningByte < fileSize);

  return {
    records,
    endByte: runningByte,
    endLine: lineOffset,
    unpaired,
    missing: false,
    rewound,
    endRunScopeId: endRunId,
    truncated,
  };
}

/**
 * Extract every tool call for one session — main transcript and all sub-agent
 * sidecars — in transcript order per file.
 */
export function extractSessionToolCalls(
  sessionId: string,
  projectsRoot: string,
  opts: ExtractOptions,
): ToolCallRecord[] {
  const files = enumerateSessionTranscripts(opts.transcriptDir, sessionId, projectsRoot);
  const out: ToolCallRecord[] = [];
  for (const file of files) out.push(...extractFromTranscript(file, opts));
  return out;
}
